import { formatMoment, t, type StaffKey, type StaffLanguage } from './staffText'
import { ApiError } from './api'
import type { Delivery, DeliveryChannel, DeliveryStatus } from './types'

/**
 * The pure logic behind Deliveries: what each row says about whom a message was
 * for and where it stands. The server has already masked anything personal
 * (internal/api viewDelivery); nothing here could un-mask it.
 */

export const CHANNEL_LABEL: Record<DeliveryChannel, StaffKey> = {
  webhook: 'deliveries.channel.webhook',
  sms: 'deliveries.channel.sms',
  email: 'deliveries.channel.email',
}

export const STATUS_LABEL: Record<DeliveryStatus, StaffKey> = {
  pending: 'deliveries.status.pending',
  delivered: 'deliveries.status.delivered',
  failed: 'deliveries.status.failed',
}

export function deliveryTarget(d: Delivery, lang: StaffLanguage): string {
  if (d.channel === 'webhook' && d.endpointGone) return t('deliveries.endpointGone', lang)
  if (d.personal && !d.target) return t(d.status === 'failed' ? 'deliveries.removedUnsent' : 'deliveries.removed', lang)
  return d.target
}

export function deliveryWhen(d: Delivery, lang: StaffLanguage): string {
  const time = (at: string) => formatMoment(at, lang, { time: true })
  const attempts = t('deliveries.attempts', lang, { n: d.attempts })
  if (d.status === 'pending' && d.nextAttemptAt) return `${attempts} · ${t('deliveries.nextAt', lang, { time: time(d.nextAttemptAt) })}`
  if (d.status === 'delivered' && d.deliveredAt) return `${attempts} · ${t('deliveries.deliveredAt', lang, { time: time(d.deliveredAt) })}`
  return attempts
}

export function failedCount(ds: readonly Delivery[]): number {
  return ds.filter((d) => d.status === 'failed').length
}

/** The server refused to send an older booking message again: a newer one exists. */
export const isSuperseded = (err: unknown) => err instanceof ApiError && err.code === 'RESEND_SUPERSEDED'

/** A superseded refusal is passed over in a batch; one pressed alone says why. */
export const skippedInBatch = (err: unknown, count: number) => count > 1 && isSuperseded(err)

const NOTES: Record<NonNullable<Delivery['resendNote']>, StaffKey> = {
  code: 'deliveries.codeNotResent',
  outdated: 'deliveries.note.outdated',
  changed: 'deliveries.note.changed',
  superseded: 'deliveries.note.superseded',
  noService: 'deliveries.note.noService',
}

/** The note on a row the server will not send again for a reason of its own. */
export function resendNote(d: Delivery, lang: StaffLanguage): string {
  const key = d.resendNote && NOTES[d.resendNote]
  return key ? t(key, lang) : ''
}

/** Sends each again, then reloads; the refusal of one pressed alone is returned for after the reload, which clears the page's error. */
export async function resendThenReload(
  ids: readonly string[],
  send: (id: string) => Promise<unknown>,
  reload: () => Promise<void>,
): Promise<{ sent: number; refusal: unknown }> {
  let sent = 0
  let refusal: unknown = undefined
  // One call each: a refusal for one must not stop the rest.
  for (const id of ids) {
    try {
      await send(id)
      sent++
    } catch (err) {
      if (!skippedInBatch(err, ids.length)) refusal = err
    }
  }
  await reload()
  return { sent, refusal }
}

// internal/mail's signInRefused, pinned there by a test of the same sentence.
const MAIL_SIGN_IN = /^(\d{3}) the mail server refused the user name or password$/

// The sender's reasons (internal/sms, webhook, mail, outbound) are recorded in English; each known shape is said in the
// reader's language and an unknown one keeps a translated prefix and the raw text.
const REASONS: [RegExp, StaffKey, (m: RegExpMatchArray) => Record<string, string>][] = [
  [/^unconfirmed, not sent again: the gateway took the request and did not answer: (.*)$/s, 'deliveries.why.gwSilent', (m) => ({ detail: m[1] ?? '' })],
  [/^unconfirmed, not sent again: the gateway answered (\d+): ?(.*)$/s, 'deliveries.why.gwUnconfirmed', (m) => ({ status: m[1] ?? '', detail: m[2] ?? '' })],
  [/^the gateway refused with (\d+): ?(.*)$/s, 'deliveries.why.gwRefused', (m) => ({ status: m[1] ?? '', detail: m[2] ?? '' })],
  [/^the gateway answered (\d+)(?: without "[^"]*")?: ?(.*)$/s, 'deliveries.why.gwAnswered', (m) => ({ status: m[1] ?? '', detail: m[2] ?? '' })],
  [/^the gateway could not be reached: (.*)$/s, 'deliveries.why.gwUnreachable', (m) => ({ detail: m[1] ?? '' })],
  [/^this organisation has no SMS gateway$/, 'deliveries.why.noGateway', () => ({})],
  [/^given up: the text has no number/, 'deliveries.why.noNumber', () => ({})],
  [/^given up: the text waited more than twenty minutes/, 'deliveries.why.tooLate', () => ({})],
  [/^the endpoint was deleted before/, 'deliveries.why.epDeleted', () => ({})],
  [/^the endpoint was switched off before/, 'deliveries.why.epOff', () => ({})],
  [/^the endpoint did not answer in time$/, 'deliveries.why.epTimeout', () => ({})],
  [/^the endpoint could not be reached(?:: .*)?$/s, 'deliveries.why.epUnreachable', () => ({})],
  [/^the endpoint answered ([^;,]*)/, 'deliveries.why.epAnswered', (m) => ({ detail: m[1] ?? '' })],
  [/^not sent: an earlier message to the same receiver failed/, 'deliveries.why.earlier', () => ({})],
  [MAIL_SIGN_IN, 'deliveries.why.mailSignIn', (m) => ({ status: m[1] ?? '' })],
  [/^the mail server refused every recipient: (.*)$/s, 'deliveries.why.mailRefused', (m) => ({ detail: m[1] ?? '' })],
]

export function failureText(raw: string, lang: StaffLanguage): string {
  const text = raw.trim()
  for (const [re, key, args] of REASONS) {
    const m = text.match(re)
    if (m) return t(key, lang, args(m))
  }
  return t('deliveries.why.other', lang, { detail: text })
}
